import { useEffect, useRef, useState } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  threshold?: number;
  resistance?: number;
  enabled?: boolean;
}

export const usePullToRefresh = ({
  onRefresh,
  threshold = 80,
  resistance = 2.5,
  enabled = true,
}: UsePullToRefreshOptions) => {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use refs to track state values for use in event handlers
  // This prevents the effect from re-running on every state change
  const pullDistanceRef = useRef(pullDistance);
  const isRefreshingRef = useRef(isRefreshing);
  const onRefreshRef = useRef(onRefresh);

  // Keep refs in sync with state/props
  pullDistanceRef.current = pullDistance;
  isRefreshingRef.current = isRefreshing;
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    let isDragging = false;

    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger if at the top of the page
      if (window.scrollY > 0) return;

      // Only trigger if scrollable content is at the top
      const scrollableParent = findScrollableParent(e.target as HTMLElement);
      if (scrollableParent && scrollableParent.scrollTop > 0) return;

      startY.current = e.touches[0].clientY;
      isDragging = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Use ref for isRefreshing to avoid stale closure
      if (!isDragging || isRefreshingRef.current) return;

      currentY.current = e.touches[0].clientY;
      const distance = currentY.current - startY.current;

      // Only pull down, not up
      if (distance > 0) {
        // Apply resistance to the pull
        const adjustedDistance = distance / resistance;
        setPullDistance(adjustedDistance);
        setIsPulling(adjustedDistance > 10);

        // Prevent default scroll behavior when pulling
        if (adjustedDistance > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (!isDragging) return;

      isDragging = false;

      // Use refs to get current values and avoid stale closure issues
      const currentPullDistance = pullDistanceRef.current;
      const currentIsRefreshing = isRefreshingRef.current;

      if (currentPullDistance > threshold && !currentIsRefreshing) {
        setIsRefreshing(true);
        try {
          await onRefreshRef.current();
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
          setIsPulling(false);
        }
      } else {
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let current: HTMLElement | null = element;
      while (current) {
        const overflow = window.getComputedStyle(current).overflow;
        if (overflow === 'auto' || overflow === 'scroll') {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  // Only include stable values in dependencies - refs handle the changing state
  }, [enabled, threshold, resistance]);

  return {
    containerRef,
    isPulling,
    isRefreshing,
    pullDistance,
  };
};
